import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from "@headlessui/react"
import {
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  EnvelopeIcon,
  HomeIcon,
  NewspaperIcon,
  XMarkIcon
} from "@heroicons/react/24/outline"
import { useInvites, useUser } from "api/ApiHooks"
import { useSidebarStore } from "hooks/useSidebar"
import { classNames } from "utils/ClassNames"
import { UniqueGradient } from "./UniqueGradient"
import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { LoversLiveForeverLogo } from "./LoversLiveForeverLogo"
import { TimeCapsuleSidebarList } from "./TimeCapsuleSidebarList"

const navigation = [
  { name: "Home", href: "/dashboard", icon: HomeIcon },
  { name: "Latest & greatest", href: "/latest", icon: NewspaperIcon },
  { name: "Invites", href: "/invites", icon: EnvelopeIcon }
]

const NavigationItems = (props: { onLinkClick?: () => void; collapsed?: boolean }) => {
  const { onLinkClick, collapsed = false } = props
  const invitesQuery = useInvites()
  const location = useLocation()

  return (
    <ul role="list" className="flex flex-1 flex-col gap-y-7">
      <li>
        <ul role="list" className="-mx-2 space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={() => (onLinkClick ? onLinkClick() : undefined)}
                title={collapsed ? item.name : undefined}
                className={classNames(
                  item.href === location.pathname
                    ? "bg-gray-50 text-primary-600 dark:bg-gray-800 dark:text-primary-400"
                    : "text-gray-700 hover:bg-gray-50 hover:text-primary-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-primary-400",
                  "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                  collapsed && "justify-center"
                )}
              >
                <div className="relative">
                  <item.icon
                    aria-hidden="true"
                    className={classNames(
                      item.href === location.pathname
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-gray-400 group-hover:text-primary-600 dark:text-gray-500 dark:group-hover:text-primary-400",
                      "h-6 w-6 shrink-0"
                    )}
                  />
                  {item.name === "Invites" &&
                    invitesQuery.data?.invites &&
                    invitesQuery.data.invites.length > 0 && (
                      <span className="inline-flex items-center justify-center h-5 w-5 bg-red-500 rounded-full text-white text-xs font-medium absolute -top-1 -right-2">
                        {invitesQuery.data.invites.length > 99
                          ? "99+"
                          : invitesQuery.data.invites.length}
                      </span>
                    )}
                </div>

                {!collapsed && item.name}
              </Link>
            </li>
          ))}
        </ul>
      </li>

      {!collapsed && (
        <li>
          <TimeCapsuleSidebarList onClick={onLinkClick} />
        </li>
      )}
    </ul>
  )
}

const UserProfile: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const { data } = useUser()
  return (
    <Link
      to="/profile"
      title={collapsed ? data?.user.name : undefined}
      className={classNames(
        "flex items-center gap-x-4 py-3 text-sm/6 font-semibold text-gray-900 lg:hover:bg-gray-50 dark:text-white dark:lg:hover:bg-gray-800",
        collapsed ? "justify-center px-0" : "lg:px-6"
      )}
    >
      {data?.user.profileImageUrl ? (
        <img
          src={data.user.profileImageUrl}
          alt=""
          className="h-8 w-8 rounded-full bg-gray-50"
        />
      ) : (
        <UniqueGradient id={data?.user.id} className="h-8 w-8 rounded-full bg-gray-50" />
      )}
      <span className="sr-only">Your profile</span>
      {!collapsed && (
        <span aria-hidden="true" className="hidden lg:block">
          {data?.user.name}
        </span>
      )}
    </Link>
  )
}

const MobileSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const { data } = useUser()

  return (
    <>
      <Dialog
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2 dark:bg-gray-900">
              <div className="mt-4 items-center">
                <LoversLiveForeverLogo className="h-14 w-auto" />
              </div>
              <nav className="flex flex-1 flex-col">
                <NavigationItems onLinkClick={() => setSidebarOpen(false)} />
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="sticky top-0 z-40 flex items-center justify-between gap-x-6 bg-white px-4 py-4 shadow-sm dark:bg-gray-900 dark:shadow-gray-800 sm:px-6 lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>
        <div>
          <LoversLiveForeverLogo className="h-14 w-auto" />
        </div>
        <div className="text-right">
          <span className="sr-only">Your profile</span>
          <Link to="/profile">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              hello
            </p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {data?.user.name}
            </p>
          </Link>
        </div>
      </div>
    </>
  )
}

const DesktopSidebar = () => {
  const collapsed = useSidebarStore((state) => state.collapsed)
  const toggleCollapsed = useSidebarStore((state) => state.toggleCollapsed)

  return (
    <div
      className={classNames(
        "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-[width] duration-300 ease-in-out",
        collapsed ? "lg:w-20" : "lg:w-72"
      )}
    >
      <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 dark:border-gray-800 dark:bg-gray-900">
        <div
          className={classNames(
            "mt-4 flex items-center",
            collapsed ? "justify-center" : "justify-between"
          )}
        >
          {!collapsed && <LoversLiveForeverLogo className="h-14 w-auto" />}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-primary-400"
          >
            <span className="sr-only">
              {collapsed ? "Expand sidebar" : "Collapse sidebar"}
            </span>
            {collapsed ? (
              <ChevronDoubleRightIcon aria-hidden="true" className="h-6 w-6" />
            ) : (
              <ChevronDoubleLeftIcon aria-hidden="true" className="h-6 w-6" />
            )}
          </button>
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <NavigationItems collapsed={collapsed} />
            <li className="-mx-6 mt-auto">
              <UserProfile collapsed={collapsed} />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

export const Sidebar = () => {
  return (
    <>
      <MobileSidebar />
      <DesktopSidebar />
    </>
  )
}
